export const MAX_CONSECUTIVE_STATUS_FAILURES = 20

interface StatusPollFailureRecovery {
  consecutiveFailures: number
  shouldPause: boolean
}

interface RefundedStatus {
  status: string
  refunded?: boolean
  /** 'preview' on the free-watermarked-preview experiment's one free run. */
  deliveryMode?: 'full' | 'preview' | null
}

const REFUNDED_TERMINAL_STATUSES = new Set(['FAILED', 'ERROR', 'REJECTED'])

/** A provider failure becomes terminal only with the server's commit witness. */
export function hasCommittedRefundWitness(result: RefundedStatus): boolean {
  return (
    result.refunded === true && REFUNDED_TERMINAL_STATUSES.has(result.status)
  )
}

/**
 * Whether a failed job is over, for a poll that must not spin forever.
 *
 * A paid job proves it with the committed refund. A free preview has no
 * refund to commit — nothing was charged — so its witness is the delivery
 * mode: the server has released the one-shot grant by the time it answers
 * with this, and the job is as finished as a refunded one. Without the second
 * arm, a failed preview left the studio polling a terminal job forever behind
 * "Finalizing your credit refund…", and its pending marker resumed that same
 * dead job on the next page load.
 */
export function hasTerminalFailureWitness(result: RefundedStatus): boolean {
  if (!REFUNDED_TERMINAL_STATUSES.has(result.status)) return false
  return result.refunded === true || result.deliveryMode === 'preview'
}

/**
 * Status transport failures are recoverable: they may pause the in-memory
 * poll, but they never prove that the paid provider job reached a terminal
 * state. The caller must therefore leave its persisted request id intact.
 */
export function recordStatusPollFailure(
  consecutiveFailures: number,
): StatusPollFailureRecovery {
  const nextFailures = consecutiveFailures + 1
  return {
    consecutiveFailures: nextFailures,
    shouldPause: nextFailures >= MAX_CONSECUTIVE_STATUS_FAILURES,
  }
}
